/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Page, Card, Text, BlockStack, Banner } from "@shopify/polaris";
import { AlertTriangleIcon } from "@shopify/polaris-icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useFetchStaffAnalyticsQuery } from "../store/store/slices/DashboardRTK";
import DateRange from "../components/atoms/DateRange";
import dayjs from "dayjs";

// Helper function to get default date ranges (same as DateRange component)
const getDefaultRanges = () => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  return [
    { title: "Today", alias: "today", period: { since: new Date(today), until: new Date(today) } },
    {
      title: "Yesterday",
      alias: "yesterday",
      period: { since: new Date(yesterday), until: new Date(yesterday) },
    },
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 6)), // 7 days including today
        until: new Date(today), // Include today
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 30)),
        until: yesterday,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 90)),
        until: yesterday,
      },
    },
    {
      title: "Last 365 days",
      alias: "last365days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 365)),
        until: yesterday,
      },
    },
    {
      title: "Last 12 months",
      alias: "last12months",
      period: {
        since: new Date(today.getFullYear(), today.getMonth() - 12, today.getDate()),
        until: yesterday,
      },
    },
    {
      title: "Last week",
      alias: "lastweek",
      period: {
        since: new Date(new Date().setDate(today.getDate() - today.getDay() - 7)),
        until: new Date(new Date().setDate(today.getDate() - today.getDay() - 1)),
      },
    },
    {
      title: "Last month",
      alias: "lastmonth",
      period: {
        since: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        until: new Date(today.getFullYear(), today.getMonth(), 0),
      },
    },
  ];
};

// Helper function to get quarter ranges for the current year
const getQuarterRanges = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-12 (January = 1, December = 12)
  
  const createQuarterDate = (year: number, month: number, day: number, isEnd: boolean = false): Date => {
    const date = new Date(year, month, day);
    if (isEnd) {
      date.setHours(23, 59, 59, 999);
    } else {
      date.setHours(0, 0, 0, 0);
    }
    return date;
  };

  const quarters = [
    {
      title: `Q1 ${currentYear}`,
      alias: `q1-${currentYear}`,
      period: {
        since: createQuarterDate(currentYear, 0, 1), // January 1
        until: createQuarterDate(currentYear, 2, 31, true), // March 31
      },
      quarterNumber: 1,
    },
    {
      title: `Q2 ${currentYear}`,
      alias: `q2-${currentYear}`,
      period: {
        since: createQuarterDate(currentYear, 3, 1), // April 1
        until: createQuarterDate(currentYear, 5, 30, true), // June 30
      },
      quarterNumber: 2,
    },
    {
        title: `Q3 ${currentYear}`,
      alias: `q3-${currentYear}`,
      period: {
        since: createQuarterDate(currentYear, 6, 1), // July 1
        until: createQuarterDate(currentYear, 8, 30, true), // September 30
      },
      quarterNumber: 3,
    },
    {
      title: `Q4 ${currentYear}`,
      alias: `q4-${currentYear}`,
      period: {
        since: createQuarterDate(currentYear, 9, 1), // October 1
        until: createQuarterDate(currentYear, 11, 31, true), // December 31
      },
      quarterNumber: 4,
    },
  ];

  // Filter quarters to only show those that have passed
  // Q1 ends in month 3, Q2 ends in month 6, Q3 ends in month 9, Q4 ends in month 12
  return quarters.filter((quarter) => {
    const quarterEndMonth = quarter.quarterNumber * 3; // Q1 = 3, Q2 = 6, Q3 = 9, Q4 = 12
    return currentMonth > quarterEndMonth;
  });
};

// Shopify Polaris color palette
const POLARIS_COLORS = {
  primary: "#008060", // Success/Primary green
  info: "#5c6ac4", // Info blue
  critical: "#d72c0d", // Critical red
  warning: "#ffc453", // Warning yellow
  subduedBg: "#f6f6f7", // Subdued background
  border: "#e1e3e5", // Border color
  textSubdued: "#6d7175", // Subdued text
  surface: "#ffffff", // Surface white
};

// Staff chart colors - lightish pastel blues
const STAFF_COLORS = [
  "#ADD8E6", // Light blue
  "#B0E0E6", // Powder blue
  "#AFEEEE", // Pale turquoise
  "#B8E0D2", // Light mint blue
  "#C8E6C9", // Light sea green
  "#A8D5E2", // Light sky blue
  "#B3D9FF", // Very light blue
  "#C4E1FF", // Lightest blue
];

const StaffAnalytics = () => {
  // Get date range from URL params or use default (last 30 days)
  // Use useLocation to make it reactive to URL changes
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const startDate = params.get("bookingfrom")
    ? dayjs(new Date(params.get("bookingfrom")!)).format("YYYY-MM-DD")
    : dayjs().subtract(29, "day").format("YYYY-MM-DD");
  const endDate = params.get("bookingto")
    ? dayjs(new Date(params.get("bookingto")!)).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  // Combine default ranges with quarter ranges for StaffAnalytics
  const customRanges = useMemo(() => {
    const defaultRanges = getDefaultRanges();
    const quarterRanges = getQuarterRanges();
    // Combine default ranges with quarter ranges (quarters at the end)
    return [...defaultRanges, ...quarterRanges];
  }, []);

  const {
    data: analytics,
    isLoading,
    isError,
    error,
    isFetching
  } = useFetchStaffAnalyticsQuery({
    startDate,
    endDate,
  });

  // Format staff data for chart
  const staffChartData = useMemo(() => {
    if (!analytics?.staff) return [];
    return analytics.staff
      .map((staff: any) => ({
        name: `${staff.firstName} ${staff.lastName}`,
        orders: staff.numberOfOrders || 0,
        email: staff.email,
        role: staff.role,
      }))
      .sort((a: any, b: any) => b.orders - a.orders); // Sort by orders descending
  }, [analytics]);

  // Calculate total orders
  const totalOrders = useMemo(() => {
    if (!analytics?.staff) return 0;
    return analytics.staff.reduce(
      (sum: number, staff: any) => sum + (staff.numberOfOrders || 0),
      0
    );
  }, [analytics]);

  if (isError) {
    return (
      <Page title="Staff Analytics">
        <Banner icon={AlertTriangleIcon} tone="warning">
          {(error as any)?.data?.errors?.[0]?.msg || "Failed to load staff analytics"}
        </Banner>
      </Page>
    );
  }

  return (
    <Page title="Staff Analytics" titleHidden>
      <div style={{ padding: "20px 0" }}>
        {/* Date Range Filter */}
        <div style={{ marginBottom: "24px" }}>
          <DateRange customRanges={customRanges} />
        </div>
        <Card>
          <div style={{ minHeight: "400px" }}>
            <BlockStack gap="400">
              {isLoading || isFetching ? (
                <div className="dashboard-skeleton-wrapper">
                  <div
                    className="dashboard-skeleton"
                    style={{
                      height: "20px",
                      width: "200px",
                      marginBottom: "16px",
                      borderRadius: "4px",
                      backgroundColor: POLARIS_COLORS.subduedBg,
                    }}
                  />
                  <div
                    className="dashboard-skeleton"
                    style={{
                      height: "350px",
                      borderRadius: "4px",
                      backgroundColor: POLARIS_COLORS.subduedBg,
                    }}
                  />
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Text variant="headingMd" as="h3">
                      Orders by staff member
                    </Text>
                    <Text variant="bodyMd" tone="subdued" as="span">
                      Total orders: <strong>{totalOrders.toLocaleString()}</strong>
                    </Text>
                  </div>
                  {staffChartData.length > 0 ? (
                    <div style={{ height: "350px" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={staffChartData}
                          layout="vertical"
                          margin={{ top: 5, right: 60, left: 5, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={POLARIS_COLORS.border}
                          />
                          <XAxis
                            type="number"
                            tick={{
                              fill: POLARIS_COLORS.textSubdued,
                              fontSize: 12,
                            }}
                            axisLine={{ stroke: POLARIS_COLORS.border }}
                          />
                          <YAxis
                            dataKey="name"
                            type="category"
                            width={80}
                            tick={{
                              fill: POLARIS_COLORS.textSubdued,
                              fontSize: 11,
                            }}
                            axisLine={{ stroke: POLARIS_COLORS.border }}
                          />
                          <Tooltip
                            formatter={(value: number, _name: string, props: any) => {
                              return [
                                `${value.toLocaleString()} orders`,
                                props.payload.name,
                              ];
                            }}
                            contentStyle={{
                              backgroundColor: POLARIS_COLORS.surface,
                              border: `1px solid ${POLARIS_COLORS.border}`,
                              borderRadius: "6px",
                              padding: "8px 12px",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Bar
                            dataKey="orders"
                            radius={[0, 4, 4, 0]}
                            label={{
                              position: "right",
                              fill: POLARIS_COLORS.textSubdued,
                              fontSize: 12,
                              formatter: (value: number) =>
                                value.toLocaleString(),
                            }}
                          >
                            {staffChartData.map((_: any, index: number) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  STAFF_COLORS[index % STAFF_COLORS.length]
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div
                      style={{
                        height: "350px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Text tone="subdued" as="p">
                        No staff data available
                      </Text>
                    </div>
                  )}
                </>
              )}
            </BlockStack>
          </div>
        </Card>
      </div>
    </Page>
  );
};

export default StaffAnalytics;
